import { useState } from 'react';
import { TopBar } from '../components/layout/TopBar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { cn } from '../lib/utils';
import { Copy, Check, Code2, Globe, Key, Zap, BookOpen, ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';

const ENDPOINTS = [
  {
    group: 'Reservas', color: '#3B82F6', icon: '📅',
    routes: [
      { method: 'GET', path: '/api/reservations', desc: 'Listar todas las reservas', params: 'date, status, guest_name' },
      { method: 'POST', path: '/api/reservations', desc: 'Crear una nueva reserva', body: true },
      { method: 'GET', path: '/api/reservations/{id}', desc: 'Obtener una reserva por ID' },
      { method: 'PATCH', path: '/api/reservations/{id}', desc: 'Actualizar una reserva', body: true },
      { method: 'DELETE', path: '/api/reservations/{id}', desc: 'Cancelar una reserva' },
    ]
  },
  {
    group: 'Clientes', color: '#10B981', icon: '👥',
    routes: [
      { method: 'GET', path: '/api/customers', desc: 'Listar clientes' },
      { method: 'POST', path: '/api/customers', desc: 'Crear cliente', body: true },
      { method: 'GET', path: '/api/customers/{id}', desc: 'Perfil de cliente' },
      { method: 'PATCH', path: '/api/customers/{id}', desc: 'Actualizar cliente', body: true },
    ]
  },
  {
    group: 'Mesas y Zonas', color: '#8B5CF6', icon: '🪑',
    routes: [
      { method: 'GET', path: '/api/tables', desc: 'Listar mesas' },
      { method: 'GET', path: '/api/zones', desc: 'Listar zonas' },
      { method: 'GET', path: '/api/tables/availability', desc: 'Consultar disponibilidad', params: 'date, time, party_size' },
    ]
  },
  {
    group: 'Lista de Espera', color: '#F59E0B', icon: '⏳',
    routes: [
      { method: 'GET', path: '/api/waitlist', desc: 'Ver lista de espera' },
      { method: 'POST', path: '/api/waitlist', desc: 'Añadir a lista de espera', body: true },
      { method: 'PATCH', path: '/api/waitlist/{id}', desc: 'Actualizar estado', body: true },
    ]
  },
  {
    group: 'Eventos', color: '#EC4899', icon: '🎉',
    routes: [
      { method: 'GET', path: '/api/events', desc: 'Listar eventos' },
      { method: 'POST', path: '/api/events', desc: 'Crear evento', body: true },
      { method: 'GET', path: '/api/events/{id}', desc: 'Detalle de evento' },
    ]
  },
  {
    group: 'Analíticas', color: '#6366F1', icon: '📊',
    routes: [
      { method: 'GET', path: '/api/analytics/overview', desc: 'Métricas generales' },
      { method: 'GET', path: '/api/analytics/turn-times', desc: 'Heatmap de tiempos de rotación por hora/día + desglose por tamaño de grupo', params: 'limit' },
      { method: 'GET', path: '/api/nps/stats', desc: 'Estadísticas NPS' },
    ]
  },
  {
    group: 'OkWallet', color: '#10B981', icon: '👛',
    routes: [
      { method: 'GET', path: '/api/wallet/me', desc: 'Obtener cartera del restaurante (balance y moneda)' },
      { method: 'GET', path: '/api/wallet/transactions', desc: 'Historial de transacciones de la cartera', params: 'limit, skip' },
      { method: 'POST', path: '/api/wallet/deposit', desc: 'Agregar fondos a la cartera', body: true },
      { method: 'POST', path: '/api/wallet/charge', desc: 'Cobrar desde la cartera (solo admin)', body: true },
    ]
  },
  {
    group: 'Acceso Garantizado — Planes', color: '#8B5CF6', icon: '👑',
    routes: [
      { method: 'GET', path: '/api/nuevas_funciones/acceso-garantizado/plans', desc: 'Listar todos los planes de membresía' },
      { method: 'POST', path: '/api/nuevas_funciones/acceso-garantizado/plans', desc: 'Crear un nuevo plan de membresía', body: true },
      { method: 'PUT', path: '/api/nuevas_funciones/acceso-garantizado/plans/{plan_id}', desc: 'Actualizar un plan existente', body: true },
      { method: 'DELETE', path: '/api/nuevas_funciones/acceso-garantizado/plans/{plan_id}', desc: 'Eliminar un plan' },
    ]
  },
  {
    group: 'Multi-Moneda', color: '#F59E0B', icon: '💱',
    routes: [
      { method: 'GET', path: '/api/restaurant', desc: 'Incluye campo `currency` con código ISO-4217 de la moneda activa (e.g. MXN, USD, EUR)' },
      { method: 'PATCH', path: '/api/restaurant', desc: 'Actualizar moneda del restaurante enviando `{ "currency": "USD" }` en el body', body: true },
    ]
  },
  {
    group: 'Menús Digitales', color: '#EC4899', icon: '🍽️',
    routes: [
      { method: 'GET', path: '/api/menus', desc: 'Listar menús del restaurante' },
      { method: 'POST', path: '/api/menus', desc: 'Crear menú', body: true },
      { method: 'GET', path: '/api/menus/{menu_id}', desc: 'Detalle del menú con secciones y platillos' },
      { method: 'PATCH', path: '/api/menus/{menu_id}', desc: 'Actualizar menú', body: true },
      { method: 'DELETE', path: '/api/menus/{menu_id}', desc: 'Eliminar menú' },
      { method: 'GET', path: '/api/menus/{menu_id}/dishes', desc: 'Listar platillos de un menú', params: 'section' },
      { method: 'POST', path: '/api/menus/{menu_id}/dishes', desc: 'Crear platillo en un menú', body: true },
      { method: 'PATCH', path: '/api/menus/{menu_id}/dishes/{dish_id}', desc: 'Actualizar platillo', body: true },
      { method: 'DELETE', path: '/api/menus/{menu_id}/dishes/{dish_id}', desc: 'Eliminar platillo' },
    ]
  },
];

const METHOD_COLORS = {
  GET: { bg: 'bg-blue-100', text: 'text-blue-700' },
  POST: { bg: 'bg-green-100', text: 'text-green-700' },
  PATCH: { bg: 'bg-amber-100', text: 'text-amber-700' },
  DELETE: { bg: 'bg-red-100', text: 'text-red-700' },
  PUT: { bg: 'bg-purple-100', text: 'text-purple-700' },
};

const EXAMPLE_RESPONSE = `{
  "id": "res_abc123",
  "guest_name": "María García",
  "guest_email": "maria@example.com",
  "guest_phone": "+34 612 345 678",
  "date": "2026-04-15",
  "time": "21:00",
  "party_size": 4,
  "status": "confirmed",
  "table_id": "table_01",
  "notes": "Alergia a frutos secos",
  "created_at": "2026-04-10T10:30:00Z"
}`;

const EXAMPLE_CURL = `curl -X GET \\
  'https://api.okreserva.com/api/reservations?date=2026-04-15' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -H 'Content-Type: application/json'`;

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <button onClick={copy} className="p-1 rounded hover:bg-white/10 transition-colors">
      {copied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5 text-slate-400" />}
    </button>
  );
}

export default function APIDocumentation() {
  const [expanded, setExpanded] = useState({});
  const [activeGroup, setActiveGroup] = useState(null);

  const toggle = (key) => setExpanded(e => ({ ...e, [key]: !e[key] }));

  const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin.replace(':5000', ':8000');

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar title="Documentación API" actions={
        <a href={`${apiBase}/docs`} target="_blank" rel="noreferrer">
          <Button variant="outline" size="sm" className="h-8 text-xs">
            <ExternalLink className="h-3.5 w-3.5 mr-1" /> Swagger UI
          </Button>
        </a>
      } />

      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar nav */}
        <div className="w-52 flex-shrink-0 bg-white border-r overflow-y-auto py-4">
          <div className="px-4 mb-4">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Recursos</p>
            {ENDPOINTS.map(g => (
              <button key={g.group} onClick={() => setActiveGroup(activeGroup === g.group ? null : g.group)}
                className={cn("w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all mb-0.5",
                  activeGroup === g.group ? "bg-orange-50 text-orange-700" : "text-slate-600 hover:bg-slate-50 hover:text-slate-800")}>
                <span>{g.icon}</span>
                {g.group}
              </button>
            ))}
          </div>
          <div className="px-4 border-t pt-4">
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">Referencias</p>
            {['Autenticación', 'Rate Limits', 'Errores', 'Webhooks'].map(l => (
              <button key={l} className="w-full text-left px-3 py-2 rounded-xl text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition-all">
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto p-8 space-y-8">
            {/* Hero */}
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center">
                  <Code2 className="h-5 w-5" />
                </div>
                <div>
                  <h1 className="text-xl font-black">OkReserva API</h1>
                  <p className="text-slate-400 text-xs">REST API v2.1 · JSON</p>
                </div>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed mb-6">
                Integra OkReserva con tus herramientas y sistemas. Accede a reservas, clientes, mesas y mucho más a través de nuestra API REST.
              </p>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Globe, l: 'Base URL', v: 'api.okreserva.com' },
                  { icon: Key, l: 'Autenticación', v: 'Bearer Token' },
                  { icon: Zap, l: 'Rate limit', v: '100 req/min' },
                ].map(i => (
                  <div key={i.l} className="bg-white/5 rounded-xl p-3 flex items-center gap-2">
                    <i.icon className="h-4 w-4 text-orange-400 flex-shrink-0" />
                    <div>
                      <p className="text-[10px] text-slate-400">{i.l}</p>
                      <p className="text-xs font-mono font-semibold text-white">{i.v}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Authentication */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Key className="h-4 w-4 text-orange-500" /> Autenticación</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-slate-600">Incluye tu API key en el header de cada petición:</p>
                <div className="bg-slate-900 rounded-xl p-4 flex items-start justify-between gap-2">
                  <code className="text-xs text-green-400 font-mono">Authorization: Bearer YOUR_API_KEY</code>
                  <CopyBtn text="Authorization: Bearer YOUR_API_KEY" />
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                  Mantén tu API key segura. Nunca la compartas en código público ni repositorios. Genera una nueva key desde Ajustes → Integraciones → API.
                </div>
              </CardContent>
            </Card>

            {/* Quick example */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-3"><CardTitle className="text-sm flex items-center gap-2"><Zap className="h-4 w-4 text-orange-500" /> Ejemplo rápido</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-slate-600">Obtener reservas del día con cURL:</p>
                <div className="bg-slate-900 rounded-xl p-4 flex items-start justify-between gap-2 overflow-x-auto">
                  <pre className="text-xs text-green-400 font-mono whitespace-pre">{EXAMPLE_CURL}</pre>
                  <CopyBtn text={EXAMPLE_CURL} />
                </div>
                <p className="text-xs text-slate-600">Respuesta:</p>
                <div className="bg-slate-900 rounded-xl p-4 flex items-start justify-between gap-2 overflow-x-auto">
                  <pre className="text-xs text-blue-300 font-mono whitespace-pre">{EXAMPLE_RESPONSE}</pre>
                  <CopyBtn text={EXAMPLE_RESPONSE} />
                </div>
              </CardContent>
            </Card>

            {/* Endpoints */}
            <div>
              <h2 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-orange-500" /> Endpoints
              </h2>
              <div className="space-y-4">
                {ENDPOINTS.filter(g => !activeGroup || g.group === activeGroup).map(group => (
                  <Card key={group.group} className="border-0 shadow-sm overflow-hidden">
                    <button onClick={() => toggle(group.group)}
                      className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors text-left">
                      <span className="text-xl">{group.icon}</span>
                      <div className="flex-1">
                        <h3 className="text-sm font-bold text-slate-800">{group.group}</h3>
                        <p className="text-xs text-slate-400">{group.routes.length} endpoints</p>
                      </div>
                      {expanded[group.group] ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                    </button>
                    {expanded[group.group] && (
                      <div className="border-t">
                        {group.routes.map((route, i) => {
                          const mc = METHOD_COLORS[route.method] || METHOD_COLORS.GET;
                          return (
                            <div key={i} className="flex items-start gap-4 px-4 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50/50">
                              <span className={cn("text-[11px] font-black px-2.5 py-0.5 rounded-md flex-shrink-0 font-mono mt-0.5", mc.bg, mc.text)}>
                                {route.method}
                              </span>
                              <div className="flex-1 min-w-0">
                                <code className="text-xs font-mono text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{route.path}</code>
                                <p className="text-xs text-slate-500 mt-1">{route.desc}</p>
                                {route.params && <p className="text-[10px] text-slate-400 mt-0.5">Params: <code className="font-mono">{route.params}</code></p>}
                                {route.body && <p className="text-[10px] text-slate-400 mt-0.5">Requiere body JSON</p>}
                              </div>
                              <CopyBtn text={`${apiBase}${route.path}`} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>

            {/* SDKs */}
            <Card className="border-0 shadow-sm">
              <CardContent className="p-6">
                <h3 className="text-sm font-bold text-slate-800 mb-3">🛠️ SDKs y Librerías</h3>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { l: 'JavaScript / Node.js', v: 'npm install @okreserva/sdk', icon: '📦' },
                    { l: 'Python', v: 'pip install okreserva', icon: '🐍' },
                    { l: 'PHP', v: 'composer require okreserva/sdk', icon: '🐘' },
                  ].map(sdk => (
                    <div key={sdk.l} className="p-3 bg-slate-50 rounded-xl">
                      <p className="text-xs font-semibold text-slate-700 mb-1">{sdk.icon} {sdk.l}</p>
                      <code className="text-[11px] text-slate-500 font-mono">{sdk.v}</code>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
